import { StoreService } from '@hb/services/store/store.service';
import { AccordionStep, FieldSubject, FinancialObligationOption, FinancialObligationOptions } from '@hb/domain';
import { FinancialObligationsBuilder } from '@hb/builder';
import { AccordionStateService } from '@hb/services/accordion-state/accordion-state.service';
import { AbstractStoreStepInject } from '@hb/services/store/abstract-store-step-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, skip } from 'rxjs';

import { CreditCard, FinancialObligation, PersonalLoan, PrivateLease } from '@ap/ap-interface-apix-hypotheken-berekening';
import { Money } from '@ap/interface-bspc-core';

@Injectable({
    providedIn: 'root'
})
export class FinancialObligationsStoreService extends AbstractStoreStepInject implements OnDestroy {
    protected readonly storeService = inject(StoreService);
    protected readonly accordionStateService = inject(AccordionStateService);

    private readonly financialObligationsSubject: BehaviorSubject<FinancialObligation>;
    private readonly financialObligationsOptionsSubject: BehaviorSubject<FinancialObligationOptions>;

    private readonly studentDebtSubject: FieldSubject<Money>;
    private readonly paymentAccountOverdraftSubject: FieldSubject<Money>;
    private readonly mobilePhonePaymentSubject: FieldSubject<Money>;
    private readonly openEndedCreditSubject: FieldSubject<Money>;
    private readonly creditCardSubject: FieldSubject<CreditCard>;
    private readonly privateLeaseSubject: FieldSubject<PrivateLease>;
    private readonly personalLoanSubject: FieldSubject<PersonalLoan>;
    private readonly partnerAlimonySubject: FieldSubject<Money>;

    constructor() {
        super();
        this.summary = FinancialObligationOption.SUMMARY;
        this.activeOptionSubject = new BehaviorSubject(
            this.accordionStateService.getStepIsFinished(AccordionStep.FINANCIAL_OBLIGATION)
                ? FinancialObligationOption.SUMMARY
                : FinancialObligationOption.OPTIONS
        );
        const financialObligations = this.getFinancialObligationState();
        const options = this.getFinancialObligationOptionsState();
        this.financialObligationsSubject = new BehaviorSubject<FinancialObligation>(financialObligations);
        this.financialObligationsOptionsSubject = new BehaviorSubject<FinancialObligationOptions>(options);
        this.studentDebtSubject = new FieldSubject<Money>(financialObligations?.student_debt_amount);
        this.paymentAccountOverdraftSubject = new FieldSubject<Money>(financialObligations?.payment_account_overdraft_amount);
        this.mobilePhonePaymentSubject = new FieldSubject<Money>(financialObligations?.mobile_phone_payment_amount);
        this.openEndedCreditSubject = new FieldSubject<Money>(financialObligations?.open_ended_credit_amount);
        this.creditCardSubject = new FieldSubject<CreditCard>(financialObligations?.credit_card);
        this.privateLeaseSubject = new FieldSubject<PrivateLease>(financialObligations?.private_lease);
        this.personalLoanSubject = new FieldSubject<PersonalLoan>(financialObligations?.personal_loan);
        this.partnerAlimonySubject = new FieldSubject<Money>(financialObligations?.partner_alimony_amount);
        this.setUp();
    }

    setUp(): void {
        this.getHasCoApplicant()
            .pipe(distinctUntilChanged(), skip(1))
            .subscribe(() => this.incomeUpdate());
        this.getFinancialObligations()
            .pipe(skip(1))
            .subscribe((financialObligations: FinancialObligation) => this.storeService.setFinancialObligation(financialObligations));
        this.getFinancialObligationsOptions()
            .pipe(skip(1))
            .subscribe((options: FinancialObligationOptions) => {
                this.resetValues(options);
                this.storeService.setFinancialObligationOptions(options);
            });
        this.getStudentDebt().subscribe(() => this.update());
        this.getPaymentAccountOverdraft().subscribe(() => this.update());
        this.getMobilePhonePayment().subscribe(() => this.update());
        this.getOpenEndedCredit().subscribe(() => this.update());
        this.getCreditCard().subscribe(() => this.update());
        this.getPrivateLease().subscribe(() => this.update());
        this.getPersonalLoan().subscribe(() => this.update());
        this.getPartnerAlimony().subscribe(() => this.update());
    }

    getPartnerAlimony(): Observable<Money> {
        return this.partnerAlimonySubject.asObservable();
    }

    getPersonalLoan(): Observable<PersonalLoan> {
        return this.personalLoanSubject.asObservable();
    }

    getPrivateLease(): Observable<PrivateLease> {
        return this.privateLeaseSubject.asObservable();
    }

    getCreditCard(): Observable<CreditCard> {
        return this.creditCardSubject.asObservable();
    }

    getOpenEndedCredit(): Observable<Money> {
        return this.openEndedCreditSubject.asObservable();
    }

    getMobilePhonePayment(): Observable<Money> {
        return this.mobilePhonePaymentSubject.asObservable();
    }

    getFinancialObligations(): Observable<FinancialObligation> {
        return this.financialObligationsSubject.asObservable();
    }

    getFinancialObligationsOptions(): Observable<FinancialObligationOptions> {
        return this.financialObligationsOptionsSubject.asObservable();
    }

    getStudentDebt(): Observable<Money> {
        return this.studentDebtSubject.asObservable();
    }

    getPaymentAccountOverdraft(): Observable<Money> {
        return this.paymentAccountOverdraftSubject.asObservable();
    }

    ngOnDestroy(): void {
        this.activeOptionSubject.unsubscribe();
        this.financialObligationsSubject.unsubscribe();
        this.financialObligationsOptionsSubject.unsubscribe();
        this.paymentAccountOverdraftSubject.unsubscribe();
        this.mobilePhonePaymentSubject.unsubscribe();
        this.openEndedCreditSubject.unsubscribe();
        this.studentDebtSubject.unsubscribe();
        this.creditCardSubject.unsubscribe();
        this.privateLeaseSubject.unsubscribe();
        this.personalLoanSubject.unsubscribe();
        this.partnerAlimonySubject.unsubscribe();
    }

    setFinancialObligationsOptions(form: FinancialObligationOptions): void {
        this.financialObligationsOptionsSubject.next(form);
    }

    setStudentDebt(studentDebt: Money): void {
        this.studentDebtSubject.next(studentDebt);
    }

    setPaymentAccountOverdraft(paymentAccountOverdraft: Money): void {
        this.paymentAccountOverdraftSubject.next(paymentAccountOverdraft);
    }

    setMobilePhonePayment(mobilePhonePayment: Money): void {
        this.mobilePhonePaymentSubject.next(mobilePhonePayment);
    }

    setOpenEndedCredit(openEndedCredit: Money): void {
        this.openEndedCreditSubject.next(openEndedCredit);
    }

    setCreditCard(creditCard: CreditCard): void {
        this.creditCardSubject.next(creditCard);
    }

    setPrivateLease(privateLease: PrivateLease): void {
        this.privateLeaseSubject.next(privateLease);
    }

    setPersonalLoan(personalLoan: PersonalLoan): void {
        this.personalLoanSubject.next(personalLoan);
    }

    setPartnerAlimony(partnerAlimony: Money): void {
        this.partnerAlimonySubject.next(partnerAlimony);
    }

    getFinancialObligationState(): FinancialObligation {
        return this.storeService.getState()?.financialObligations;
    }

    getFinancialObligationOptionsState(): FinancialObligationOptions {
        return this.storeService.getState()?.financialObligationsOptions;
    }

    private update(): void {
        this.financialObligationsSubject.next(
            new FinancialObligationsBuilder()
                .withStudentDebt(this.studentDebtSubject.value)
                .withPaymentAccountOverdraft(this.paymentAccountOverdraftSubject.value)
                .withMobilePhonePayment(this.mobilePhonePaymentSubject.value)
                .withOpenEndedCredit(this.openEndedCreditSubject.value)
                .withCreditCard(this.creditCardSubject.value)
                .withPrivateLease(this.privateLeaseSubject.value)
                .withPersonalLoan(this.personalLoanSubject.value)
                .withPartnerAlimony(this.partnerAlimonySubject.value)
                .build()
        );
    }

    private resetValues(options: FinancialObligationOptions): void {
        const state = this.getFinancialObligationState();
        const resetStudentDebt = this.resetStudentDebt(state, options);
        const resetPaymentAccountOverdraft = this.resetPaymentAccountOverdraft(state, options);

        const resetMobilePhonePayment = this.resetMobilePhonePayment(state, options);
        const resetOpenEndedCredit = this.resetOpenEndedCredit(state, options);
        const resetCreditCard = this.resetCreditCard(state, options);
        const resetPrivateLease = this.resetPrivateLease(state, options);
        const resetPersonalLoan = this.resetPersonalLoan(state, options);
        const resetPartnerAlimony = this.resetPartnerAlimony(state, options);

        if (
            resetStudentDebt ||
            resetPaymentAccountOverdraft ||
            resetMobilePhonePayment ||
            resetOpenEndedCredit ||
            resetCreditCard ||
            resetPrivateLease ||
            resetPersonalLoan ||
            resetPartnerAlimony
        ) {
            this.update();
        }
    }

    private resetPartnerAlimony(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.partnerAlimony) {
            if (state?.partner_alimony_amount) {
                this.partnerAlimonySubject.next(null);
                return true;
            }
        }
        return false;
    }

    private resetPersonalLoan(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.personalLoan) {
            if (state?.personal_loan) {
                this.personalLoanSubject.next(null);

                return true;
            }
        }
        return false;
    }

    private resetPrivateLease(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.privateLease) {
            if (state?.private_lease) {
                this.privateLeaseSubject.next(null);
                return true;
            }
        }
        return false;
    }

    private resetCreditCard(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.creditCard) {
            if (state?.credit_card) {
                this.creditCardSubject.next(null);
                return true;
            }
        }
        return false;
    }

    private resetOpenEndedCredit(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.openEndedCredit) {
            if (state?.open_ended_credit_amount) {
                this.openEndedCreditSubject.next(null);
                return true;
            }
        }
        return false;
    }

    private resetMobilePhonePayment(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.mobilePhonePayment) {
            if (state?.mobile_phone_payment_amount) {
                this.mobilePhonePaymentSubject.next(null);
                return true;
            }
        }
        return false;
    }

    private resetPaymentAccountOverdraft(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.paymentAccountOverdraft) {
            if (state?.payment_account_overdraft_amount) {
                this.paymentAccountOverdraftSubject.next(null);
                return true;
            }
        }
        return false;
    }

    private resetStudentDebt(state: FinancialObligation, options: FinancialObligationOptions) {
        if (!options.studentDebt) {
            if (state?.student_debt_amount) {
                this.studentDebtSubject.next(null);
                return true;
            }
        }
        return false;
    }

    isActiveOption(question: FinancialObligationOption): boolean {
        switch (question) {
            case FinancialObligationOption.OPTIONS:
                return true;
            case FinancialObligationOption.STUDENT_DEBT:
                return this.financialObligationsOptionsSubject.value.studentDebt;
            case FinancialObligationOption.PAYMENT_ACCOUNT_OVERDRAFT:
                return this.financialObligationsOptionsSubject.value.paymentAccountOverdraft;
            case FinancialObligationOption.MOBILE_PHONE_PAYMENT:
                return this.financialObligationsOptionsSubject.value.mobilePhonePayment;
            case FinancialObligationOption.OPEN_ENDED_CREDIT:
                return this.financialObligationsOptionsSubject.value.openEndedCredit;
            case FinancialObligationOption.CREDIT_CARD:
                return this.financialObligationsOptionsSubject.value.creditCard;
            case FinancialObligationOption.PRIVATE_LEASE:
                return this.financialObligationsOptionsSubject.value.privateLease;
            case FinancialObligationOption.PERSONAL_LOAN:
                return this.financialObligationsOptionsSubject.value.personalLoan;
            case FinancialObligationOption.PARTNER_ALIMONY:
                return this.financialObligationsOptionsSubject.value.partnerAlimony;
            case FinancialObligationOption.SUMMARY:
            default:
                return false;
        }
    }

    private incomeUpdate(): void {
        this.accordionStateService.setStepEnabled(AccordionStep.FINANCIAL_OBLIGATION);
        this.accordionStateService.setStepNotFinished(AccordionStep.FINANCIAL_OBLIGATION);
        this.activeOptionSubject.next(FinancialObligationOption.OPTIONS);
    }
}
